import React, { useState } from 'react';
import { Mail, Phone, Clock, MapPin, Stethoscope } from 'lucide-react';
import { useForm, ValidationError } from "@formspree/react";
import { motion } from 'framer-motion';
import { ContactPageSEO } from '../../SeoTags';


// Define form data interface
interface FormData {
  name: string;
  email: string;
  phone: string;
  date: string;
  message: string;
}

// Define field configuration interface
interface FormField {
  name: keyof FormData;  // This ensures name can only be a key of FormData
  label: string;
  type: string;
  placeholder?: string;
}


const Contact = () => {
  const [state, handleSubmit] = useForm("mdkapdnp");

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleFinalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(formData);
  };

  const formFields: FormField[] = [
    { name: "name", label: "Name", type: "text", placeholder: "Enter your name" },
    { name: "email", label: "Email", type: "email", placeholder: "Enter your email" },
    { name: "phone", label: "Phone", type: "tel", placeholder: "Enter your phone number" },
    { name: "date", label: "Preferred Date", type: "date" }
  ];

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardHover = {
    hover: {
      y: -5,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <>
    <ContactPageSEO />
    <motion.main
      className="min-h-screen bg-softwhite"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <div className="relative w-full h-[500px] overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-darkbrown/95 via-charcol/90 to-brown/85"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        />
        <motion.div
          className="absolute inset-0 flex items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-softwhite mb-4"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                Schedule Your Consultation
              </motion.h1>
              <motion.p
                className="text-xl text-palegold"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                Expert Surgical Gastroenterology and Liver Transplantation Care
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Info Cards */}
      <div className="container mx-auto px-4">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 -mt-20 relative z-10"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {[
            {
              icon: <Phone className="w-6 h-6 text-sage mb-4" />,
              title: "Contact",
              content: "+917827039028"
            },
            {
              icon: <Mail className="w-6 h-6 text-sage mb-4" />,
              title: "Email",
              content: "hirak.pahari@gmail.com"
            },
            {
              icon: <Clock className="w-6 h-6 text-sage mb-4" />,
              title: "Consultation Hours",
              content: "Mon - Sat: 09:00 AM - 05:00 PM"
            }
          ].map((card, index) => (
            <motion.div
              key={index}
              className="bg-softwhite rounded-xl shadow-lg p-6 border-l-4 border-sage hover:shadow-xl transition-shadow"
              variants={fadeInUp}
              whileHover={cardHover.hover}
            >
              {card.icon}
              <h3 className="text-lg font-semibold text-darkbrown mb-2">{card.title}</h3>
              <p className="text-charcol">{card.content}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          {/* Form Section */}
          <motion.div
            className="bg-softwhite rounded-2xl shadow-xl p-8"
            whileHover={{ boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }}
            transition={{ duration: 0.3 }}
          >
            <motion.h2
              className="text-3xl font-bold text-darkbrown mb-8"
              variants={fadeInUp}
            >
              Book an Appointment
            </motion.h2>
            {state.succeeded ? (
              <motion.div
                className="text-sage text-xl text-center py-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                Thank you for your message. We'll get back to you soon!
              </motion.div>
            ) : (
              <form onSubmit={handleFinalSubmit} className="space-y-6">
                <motion.div
                  className="space-y-6"
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                >
                  {formFields.map((field) => (
                    <motion.div
                      key={field.name}
                      variants={fadeInUp}
                      whileHover={{ scale: 1.01 }}
                    >
                      <label htmlFor={field.name} className="block text-charcol text-sm font-medium mb-2">
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={formData[field.name]}  // Now TypeScript knows this is safe
                        onChange={handleChange}
                        required
                        className="w-full border-b border-sage/30 py-2 text-charcol focus:border-sage focus:outline-none bg-transparent"
                        placeholder={field.placeholder}
                      />
                      {field.name === 'email' && <ValidationError prefix="Email" field="email" errors={state.errors} />}
                    </motion.div>
                  ))}

                  <motion.div variants={fadeInUp} whileHover={{ scale: 1.01 }}>
                    <label htmlFor="message" className="block text-charcol text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full border-b border-sage/30 py-2 text-charcol focus:border-sage focus:outline-none bg-transparent resize-none"
                      placeholder="Please describe your concerns"
                    ></textarea>
                  </motion.div>
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={state.submitting}
                  className="w-full bg-darkbrown text-softwhite py-3 rounded-lg font-medium hover:bg-brown transition-colors duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {state.submitting ? 'Sending...' : 'Schedule Appointment'}
                </motion.button>
              </form>
            )}
          </motion.div>

          {/* Info Section */}
          <div className="space-y-8">
            <motion.div
              className="bg-sage/10 rounded-2xl p-8"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 }}
            >
              <h3 className="text-2xl font-bold text-darkbrown mb-6">Specialized Services</h3>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                {[
                  "Cirrhosis Management",
                  "Hepatitis B and C Treatment",
                  "Chronic Hepatitis Care",
                  "Liver Cirrhosis Treatment",
                  "Acute Liver Failure",
                  "Hepatocellular Carcinoma",
                  "Liver Transplantation"
                ].map((specialty, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center space-x-3 group"
                    variants={fadeInUp}
                    whileHover={{ x: 5 }}
                  >
                    <Stethoscope className="w-4 h-4 text-sage group-hover:text-darkbrown transition-colors" />
                    <span className="text-charcol group-hover:text-darkbrown transition-colors">{specialty}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className="bg-palegold/10 rounded-2xl p-8"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2 }}
            >
              <h3 className="text-2xl font-bold text-darkbrown mb-6">Locations & OPD Timings</h3>
              <div className="space-y-6">
                <div className="border-b border-sage/20 pb-4">
                  <div className="flex items-start space-x-3 mb-2">
                    <MapPin className="w-5 h-5 text-sage mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-darkbrown">Max Super Speciality Hospital, Shalimar Bagh</h4>
                      <p className="text-sm text-charcol/80">FC 50, Max Wali Rd, C & D Block, Shalimar Place Site, Shalimar Bagh, New Delhi - 110088</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 ml-8 text-sm text-charcol/90">
                    <Clock className="w-4 h-4 text-sage" />
                    <span>Mon - Sat: 09:00 AM - 05:00 PM</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-start space-x-3 mb-2">
                    <MapPin className="w-5 h-5 text-sage mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-darkbrown">Max Super Speciality Hospital, Patparganj</h4>
                      <p className="text-sm text-charcol/80">108A, I.P. Extension, Patparganj, New Delhi - 110092</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 ml-8 text-sm text-charcol/90">
                    <Clock className="w-4 h-4 text-sage" />
                    <span>Select Days (Thu & Fri): 09:00 AM - 05:00 PM</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.main>
    </>
  );
};

export default Contact;